import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import AppDownloadButton from './AppDownloadButton';
import googlePlayBadge from '../assets/AppStore/google play.png';
import appStoreBadge from '../assets/AppStore/applestore.png';
import '../styles/footer.css';

const Footer = () => {
    const currentYear = new Date().getFullYear();
    const navigate = useNavigate();
    const location = useLocation();
    const { user, fetchUser, redirectToLogin } = useContext(UserContext);
    const isLoggedIn = !!user;
    const userId = user?.id;
    const userUuid = user?.uuid || user?.id;  // Use UUID, fallback to ID
    
    const [isAndroid, setIsAndroid] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        // Platform detection
        const userAgent = navigator.userAgent.toLowerCase();
        const isAndroidDevice = /android/.test(userAgent);
        const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
        const isMobileDevice = /mobile|tablet|android|iphone|ipad|ipod/.test(userAgent);
        
        setIsAndroid(isAndroidDevice);
        setIsIOS(isIOSDevice);
        setIsMobile(isMobileDevice);
        
        // if (user) {
        //     fetchUser(true);
        // }
    }, [user, fetchUser]);

    return (
        <footer className="footer">
            <div className="footer-content">
                <div className="footer-sections">
                    <div className="footer-section">
                        <h3>Navigation</h3>
                        <Link to="/" className="footer-link">Accueil</Link>
                        <Link to="/products/all" className="footer-link">Tous les Produits</Link>
                        <Link to="/business" className="footer-link">Wennze Business</Link>
                        {isLoggedIn ? (
                            <Link to="/add-post" className="footer-link">Publier une Annonce</Link>
                        ) : (
                            <span className="footer-link" onClick={() => redirectToLogin('/add-post')}>
                                Publier une Annonce
                            </span>
                        )}
                    </div>

                    <div className="footer-section">
                        <h3>Mon Compte</h3>
                        {isLoggedIn ? (
                            <>
                                <Link to="/profile" className="footer-link">Mon Profil</Link>
                                <Link to={`/messages/${userUuid}/`} className="footer-link">Messages</Link>
                                <Link to="/user/likes" className="footer-link">Mes Favoris</Link>
                            </>
                        ) : (
                            <>
                                <span className="footer-link" onClick={() => redirectToLogin('/profile')}>
                                    Mon Profil
                                </span>
                                <span className="footer-link" onClick={() => redirectToLogin(`/messages/${userUuid}/`)}>
                                    Messages
                                </span>
                                <span className="footer-link" onClick={() => redirectToLogin('/user/likes')}>
                                    Mes Favoris
                                </span>
                            </>
                        )}
                    </div>

                    <div className="footer-section">
                        <h3>Découvrir</h3>
                        {/* TODO: Update these links to use category IDs from CategoryContext */}
                        <Link to="/products/all" className="footer-link">Voir tous les produits</Link>
                        {/* <Link to="/products/category/Immobilier" className="footer-link">Immobilier</Link>
                        <Link to="/products/category/Véhicules" className="footer-link">Véhicules</Link>
                        <Link to="/products/category/Électronique" className="footer-link">Électronique</Link>
                        <Link to="/products/category/Mode" className="footer-link">Mode</Link> */}
                    </div>

                    <div className="footer-section">
                        <h3>Contact & Assistance</h3>
                        <Link to="/about" className="footer-link">Qui sommes-nous</Link>
                        <Link to="/contact" className="footer-link">Contactez-nous</Link>
                        <Link to="/support" className="footer-link">Centre d'aide</Link>
                        <a 
                            href="https://wa.me/243851054526?text=Bonjour%2C%20je%20souhaite%20obtenir%20des%20informations%20sur%20Wennze.%20Pouvez-vous%20m%27aider%20%3F" 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="footer-link whatsapp-footer-link"
                        >
                            <i className="fab fa-whatsapp"></i> Assistance WhatsApp
                        </a>
                        <Link to="/terms-conditions/conditions-utilisation" className="footer-link">Conditions d'utilisation</Link>
                        <Link to="/terms-conditions/politique-confidentialite" className="footer-link">Politique de confidentialité</Link>
                    </div>
                </div>

                <div className="footer-bottom">
                    <div className="social-links">
                        <a href="https://web.facebook.com/people/Wennze-Market/61575347631099/" target="_blank" rel="noopener noreferrer" className="social-link">
                            <i className="fab fa-facebook"></i>
                        </a>
                        <a href="https://www.instagram.com/application_wennze/" target="_blank" rel="noopener noreferrer" className="social-link">
                            <i className="fab fa-instagram"></i>
                        </a>
                        <a href="https://wa.me/243851054526?text=Bonjour%2C%20je%20souhaite%20obtenir%20des%20informations%20sur%20Wennze.%20Pouvez-vous%20m%27aider%20%3F" target="_blank" rel="noopener noreferrer" className="social-link whatsapp-social">
                            <i className="fab fa-whatsapp"></i>
                        </a>
                        <div style={{ marginLeft: '16px' }}>
                            <AppDownloadButton />
                        </div>
                    </div>
                    
                    {/* App Store Badges */}
                    <div className="app-store-badges" style={{ 
                        display: 'flex', 
                        gap: '12px', 
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginTop: '20px',
                        marginBottom: '20px'
                    }}>
                        {/* Show Google Play badge for Android or all desktop users */}
                        {(!isMobile || isAndroid) && (
                            <a 
                                href="https://play.google.com/store/apps/details?id=com.wennze.app" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ display: 'block', transition: 'opacity 0.3s' }}
                                onMouseOver={(e) => e.currentTarget.style.opacity = '0.8'}
                                onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                            >
                                <img 
                                    src={googlePlayBadge} 
                                    alt="Télécharger sur Google Play" 
                                    style={{ height: '45px', width: 'auto' }}
                                />
                            </a>
                        )}
                        {/* Show App Store badge for iOS or all desktop users */}
                        {(!isMobile || isIOS) && (
                            <a 
                                href="https://apps.apple.com/app/wennze/id6755939537" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ display: 'block', transition: 'opacity 0.3s' }}
                                onMouseOver={(e) => e.currentTarget.style.opacity = '0.8'}
                                onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                            >
                                <img 
                                    src={appStoreBadge} 
                                    alt="Télécharger sur App Store" 
                                    style={{ height: '45px', width: 'auto' }}
                                />
                            </a>
                        )}
                    </div>
                    
                    <div className="footer-text">
                        <p>&copy; {currentYear} Wennze. Tous droits réservés.</p>
                        <p className="trademark-notice">
                            Wennze® est une marque déposée. Le nom, le logo et le design sont protégés par les lois sur la propriété intellectuelle.
                        </p>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;